/**
 * PURPOSE: Custom hook for annotation state and pointer/keyboard handlers
 */
import { useState, useRef, useCallback, useEffect } from 'react'
import type { AnnotationTool, Annotation, Point } from './types'
import { drawAnnotation } from './drawHelpers'

export function useAnnotation(imageUrl: string, onSave: (blob: Blob) => void) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const imageRef = useRef<HTMLImageElement | null>(null)
  const scaleRef = useRef({ x: 1, y: 1 })

  const [activeTool, setActiveTool] = useState<AnnotationTool>('draw')
  const [activeColor, setActiveColor] = useState('#ef4444')
  const [annotations, setAnnotations] = useState<Annotation[]>([])
  const [redoStack, setRedoStack] = useState<Annotation[]>([])
  const [isDrawing, setIsDrawing] = useState(false)
  const [currentAnnotation, setCurrentAnnotation] = useState<Annotation | null>(null)
  const [textInput, setTextInput] = useState('')
  const [textPosition, setTextPosition] = useState<Point | null>(null)
  const [imageLoaded, setImageLoaded] = useState(false)

  useEffect(() => {
    const img = new Image(); img.crossOrigin = 'anonymous'
    img.onload = () => { imageRef.current = img; setImageLoaded(true); resizeCanvas() }
    img.src = imageUrl
  }, [imageUrl])

  const resizeCanvas = useCallback(() => {
    const canvas = canvasRef.current, container = containerRef.current, img = imageRef.current
    if (!canvas || !container || !img) return
    const cW = container.clientWidth, cH = container.clientHeight, imgAspect = img.width / img.height, cAspect = cW / cH
    let dW: number, dH: number
    if (imgAspect > cAspect) { dW = cW; dH = cW / imgAspect } else { dH = cH; dW = cH * imgAspect }
    canvas.style.width = `${dW}px`; canvas.style.height = `${dH}px`
    canvas.width = img.width; canvas.height = img.height
    scaleRef.current = { x: img.width / dW, y: img.height / dH }
    redraw()
  }, [annotations])

  const redraw = useCallback(() => {
    const canvas = canvasRef.current, img = imageRef.current; if (!canvas || !img) return
    const ctx = canvas.getContext('2d'); if (!ctx) return
    ctx.clearRect(0, 0, canvas.width, canvas.height); ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
    for (const ann of annotations) drawAnnotation(ctx, ann)
    if (currentAnnotation) drawAnnotation(ctx, currentAnnotation)
  }, [annotations, currentAnnotation])

  useEffect(() => { redraw() }, [redraw])

  const getCanvasPoint = useCallback((clientX: number, clientY: number): Point => {
    const canvas = canvasRef.current; if (!canvas) return { x: 0, y: 0 }
    const rect = canvas.getBoundingClientRect()
    return { x: (clientX - rect.left) * scaleRef.current.x, y: (clientY - rect.top) * scaleRef.current.y }
  }, [])

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    e.preventDefault(); const point = getCanvasPoint(e.clientX, e.clientY)
    if (activeTool === 'text') { setTextPosition(point); return }
    setIsDrawing(true); setRedoStack([])
    setCurrentAnnotation({ tool: activeTool, color: activeColor, lineWidth: 3, ...(activeTool === 'draw' ? { points: [point] } : { start: point, end: point }) })
  }, [activeTool, activeColor, getCanvasPoint])

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!isDrawing || !currentAnnotation) return; e.preventDefault()
    const point = getCanvasPoint(e.clientX, e.clientY)
    setCurrentAnnotation(prev => prev ? (prev.tool === 'draw' ? { ...prev, points: [...(prev.points || []), point] } : { ...prev, end: point }) : null)
  }, [isDrawing, currentAnnotation, getCanvasPoint])

  const handlePointerUp = useCallback(() => {
    if (!isDrawing || !currentAnnotation) return; setIsDrawing(false)
    const isValid = currentAnnotation.tool === 'draw' ? (currentAnnotation.points?.length ?? 0) > 2 : currentAnnotation.start && currentAnnotation.end && (Math.abs(currentAnnotation.end.x - currentAnnotation.start.x) > 5 || Math.abs(currentAnnotation.end.y - currentAnnotation.start.y) > 5)
    if (isValid) setAnnotations(prev => [...prev, currentAnnotation])
    setCurrentAnnotation(null)
  }, [isDrawing, currentAnnotation])

  const handleTextSubmit = useCallback(() => {
    if (!textInput.trim() || !textPosition) return
    setAnnotations(prev => [...prev, { tool: 'text', color: activeColor, lineWidth: 3, text: textInput.trim(), position: textPosition }])
    setRedoStack([]); setTextInput(''); setTextPosition(null)
  }, [textInput, textPosition, activeColor])

  const handleUndo = useCallback(() => setAnnotations(prev => { if (!prev.length) return prev; setRedoStack(r => [...r, prev[prev.length - 1]]); return prev.slice(0, -1) }), [])
  const handleRedo = useCallback(() => setRedoStack(prev => { if (!prev.length) return prev; setAnnotations(a => [...a, prev[prev.length - 1]]); return prev.slice(0, -1) }), [])
  const handleClearAll = useCallback(() => { setRedoStack([...annotations]); setAnnotations([]) }, [annotations])

  const handleSave = useCallback(() => { const canvas = canvasRef.current; if (!canvas) return; canvas.toBlob(blob => { if (blob) onSave(blob) }, 'image/jpeg', 0.92) }, [onSave])

  return {
    canvasRef, containerRef, imageLoaded,
    activeTool, setActiveTool, activeColor, setActiveColor,
    annotations, redoStack, textInput, setTextInput, textPosition, setTextPosition,
    handlePointerDown, handlePointerMove, handlePointerUp,
    handleTextSubmit, handleUndo, handleRedo, handleClearAll, handleSave,
  }
}
